import React from 'react';
import { useNavigate } from 'react-router-dom';

const BumdesStepCards = ({
  isPreSurveyDone,
  isVeciDone,
  isClassified,
  isProgram,
}) => {
  const navigate = useNavigate();

  // Define steps configuration in JSON-like structure
  const steps = [
    {
      id: 1,
      title: 'Pre-Survey BUMDes',
      subtitle: 'Tahap 1',
      description:
        'Lengkapi data dasar dan kesediaan BUMDes sebagai tahap awal asesmen.',
      icon: 'bi-patch-question-fill',
      iconColor: 'text-info',
      iconBg: 'bg-info bg-opacity-10',
      isVisible: true,
      badge: isPreSurveyDone ? (
        <span className='badge bg-success bg-opacity-10 text-success rounded-pill px-3 py-1.5 fw-semibold'>
          <i className='bi bi-check-circle-fill me-1'></i> Selesai
        </span>
      ) : (
        <span className='badge bg-warning bg-opacity-10 text-warning rounded-pill px-3 py-1.5 fw-semibold animate-pulse'>
          <i className='bi bi-hourglass-split me-1'></i> Wajib
        </span>
      ),
      button: (
        <button
          onClick={() => navigate('/bumdes/pre-survey')}
          className={`btn w-100 rounded-pill py-2 fw-bold transition-all ${
            isPreSurveyDone ? 'btn-outline-secondary' : 'btn-primary shadow-sm'
          }`}
        >
          {isPreSurveyDone ? 'Lihat/Ubah Jawaban' : 'Mulai Pre-Survey'}
        </button>
      ),
    },
    {
      id: 2,
      title: 'Survei VECI',
      subtitle: 'Tahap 2',
      description:
        'Isi kuesioner kapasitas ekonomi BUMDes (VECI) secara objektif setelah menyelesaikan Pre-Survey.',
      icon: 'bi-clipboard-data-fill',
      iconColor: 'text-primary',
      iconBg: 'bg-primary bg-opacity-10',
      isVisible: true,
      badge: isVeciDone ? (
        <span className='badge bg-success bg-opacity-10 text-success rounded-pill px-3 py-1.5 fw-semibold'>
          <i className='bi bi-check-circle-fill me-1'></i> Selesai
        </span>
      ) : !isPreSurveyDone ? (
        <span className='badge bg-secondary bg-opacity-10 text-secondary rounded-pill px-3 py-1.5 fw-semibold'>
          <i className='bi bi-lock-fill me-1'></i> Terkunci
        </span>
      ) : (
        <span className='badge bg-warning bg-opacity-10 text-warning rounded-pill px-3 py-1.5 fw-semibold animate-pulse'>
          <i className='bi bi-hourglass-split me-1'></i> Wajib
        </span>
      ),
      button: (
        <button
          disabled={!isPreSurveyDone}
          onClick={() => navigate('/bumdes/veci')}
          className={`btn w-100 rounded-pill py-2 fw-bold transition-all ${
            isVeciDone
              ? 'btn-outline-secondary'
              : !isPreSurveyDone
                ? 'btn-light text-muted'
                : 'btn-primary shadow-sm'
          }`}
        >
          {!isPreSurveyDone
            ? 'Selesaikan Langkah 1 Dulu'
            : isVeciDone
              ? 'Lihat Hasil VECI'
              : 'Mulai Survei VECI'}
        </button>
      ),
    },
    {
      id: 3,
      title: 'Business Model Canvas',
      subtitle: 'Tahap 3',
      description:
        'Kelola strategi bisnis BUMDes melalui kerangka Business Model Canvas (BMC).',
      icon: 'bi-grid-1x2-fill',
      iconColor: 'text-success',
      iconBg: 'bg-success bg-opacity-10',
      isVisible: isClassified && isProgram,
      badge: (
        <span className='badge bg-success bg-opacity-10 text-success rounded-pill px-3 py-1.5 fw-semibold'>
          <i className='bi bi-unlock-fill me-1'></i> Terbuka
        </span>
      ),
      button: (
        <button
          onClick={() => navigate('/bumdes/bmc')}
          className='btn btn-primary w-100 rounded-pill py-2 fw-bold transition-all shadow-sm'
        >
          Akses BMC
        </button>
      ),
    },
  ];

  const visibleSteps = steps.filter((step) => step.isVisible);

  // Helper to determine status
  const getStepStatus = (stepId) => {
    if (stepId === 1) return isPreSurveyDone ? 'completed' : 'active';
    if (stepId === 2) {
      if (isVeciDone) return 'completed';
      if (isPreSurveyDone) return 'active';
      return 'locked';
    }
    if (stepId === 3) {
      if (isVeciDone) return 'active';
      return 'locked';
    }
    return 'locked';
  };

  // Calculate completed count for progress line (out of visible steps)
  const completedCount = visibleSteps.filter(
    (s) => getStepStatus(s.id) === 'completed',
  ).length;

  const progressPercent =
    visibleSteps.length > 1
      ? (completedCount / (visibleSteps.length - 1)) * 100
      : 100;

  return (
    <div className='w-100 mb-4'>
      {/* Visual Stepper Header (Desktop & Large Screens) */}
      <div className='position-relative mb-5 d-none d-md-block py-2'>
        <div className='position-relative w-100'>
          {/* Padded Progress Track Container */}
          <div
            className='position-absolute'
            style={{
              top: '18px',
              left: '18px',
              right: '18px',
              height: '3px',
              zIndex: 1,
            }}
          >
            {/* Background Gray Line */}
            <div
              className='w-100 h-100'
              style={{ backgroundColor: '#cbd5e1', borderRadius: '2px' }}
            ></div>
            {/* Active Blue Progress Line */}
            <div
              className='position-absolute top-0 start-0 h-100 bg-primary'
              style={{
                width: `${Math.min(progressPercent, 100)}%`,
                borderRadius: '2px',
                transition: 'width 0.4s ease-in-out',
              }}
            ></div>
          </div>

          {/* Stepper Nodes */}
          <div
            className='position-relative d-flex justify-content-between align-items-center'
            style={{ zIndex: 2 }}
          >
            {visibleSteps.map((step) => {
              const status = getStepStatus(step.id);
              const isActive = status === 'active';
              const isCompleted = status === 'completed';

              return (
                <div
                  key={step.id}
                  className='d-flex flex-column align-items-center'
                >
                  <div
                    className={`rounded-circle d-flex align-items-center justify-content-center shadow-sm transition-all`}
                    style={{
                      width: '36px',
                      height: '36px',
                      backgroundColor: isCompleted
                        ? '#10b981'
                        : isActive
                          ? '#3b82f6'
                          : '#ffffff',
                      color: isCompleted || isActive ? '#ffffff' : '#94a3b8',
                      border: isActive
                        ? '4px solid #dbeafe'
                        : isCompleted
                          ? 'none'
                          : '2px solid #cbd5e1',
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                    }}
                  >
                    {isCompleted ? <i className='bi bi-check-lg'></i> : step.id}
                  </div>
                  <span
                    className={`small fw-semibold mt-2 ${
                      isActive
                        ? 'text-primary'
                        : isCompleted
                          ? 'text-success'
                          : 'text-muted'
                    }`}
                  >
                    {step.title}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Grid of step cards */}
      <div
        className={`row g-4 row-cols-1 row-cols-md-${Math.min(visibleSteps.length, 3)}`}
      >
        {visibleSteps.map((step) => {
          const status = getStepStatus(step.id);
          const isActive = status === 'active';
          const isCompleted = status === 'completed';

          return (
            <div
              key={step.id}
              className='col'
            >
              <div
                className={`card border-0 shadow-sm rounded-4 h-100 transition-all p-4 bg-white position-relative overflow-hidden ${
                  isActive ? 'border-start border-4 border-primary' : ''
                }`}
                style={{
                  boxShadow: isActive
                    ? '0 10px 15px -3px rgba(59, 130, 246, 0.1), 0 4px 6px -2px rgba(59, 130, 246, 0.05)'
                    : '',
                  backgroundColor: status === 'locked' ? '#fafafa' : '#ffffff',
                }}
              >
                <div className='d-flex justify-content-between align-items-start mb-3'>
                  <div
                    className={`${step.iconBg} p-3 rounded-4 ${step.iconColor}`}
                  >
                    <i className={`bi ${step.icon} fs-3`}></i>
                  </div>
                  {step.badge}
                </div>
                <div className='text-muted extra-small fw-bold text-uppercase mb-1 tracking-wider'>
                  {step.subtitle}
                </div>
                <h4 className='fw-bold text-dark mb-2'>{step.title}</h4>
                <p
                  className='text-muted small mb-4 flex-grow-1'
                  style={{ minHeight: '40px' }}
                >
                  {step.description}
                </p>
                <div>{step.button}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BumdesStepCards;
